import { useMemo, useState } from "react";
import { Eye } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Loader, EmptyState } from "@/components/common/Loader";
import { FileThumb, FilePreviewDialog, DownloadButton } from "@/components/common/Files";
import { NewBadge } from "@/components/common/Badges";
import { useFetch } from "@/lib/api";
import { fmtDate, isNew, fileExt, SECTIONS } from "@/lib/format";

function docMeta(d) {
  if (d.section === "attestati") return { title: d.specialist_name, lines: [d.protocol_name, fmtDate(d.doc_date)] };
  if (d.section === "contratti")
    return { title: d.title, lines: [`Firmato il ${fmtDate(d.signed_date)}`, d.expiry_date ? `Scadenza ${fmtDate(d.expiry_date)}` : "Senza scadenza"] };
  return { title: d.title, lines: [fileExt(d.filename), fmtDate(d.created_at)] };
}

function DocRow({ d, onPreview, showSection }) {
  const m = docMeta(d);
  return (
    <div data-testid={`document-item-${d.id}`} className="lux-card p-4 sm:p-5 flex items-center gap-4">
      <button onClick={() => onPreview(d)} className="shrink-0" aria-label="Anteprima"><FileThumb kind="document" file={d} /></button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {showSection && <span className="eyebrow">{SECTIONS[d.section]}</span>}
          {isNew(d.created_at) && <NewBadge />}
        </div>
        <p className="font-serif text-lg sm:text-xl leading-snug mt-1 truncate">{m.title}</p>
        <p className="text-xs text-warm-text mt-1">{m.lines.filter(Boolean).join(" · ")}</p>
      </div>
      <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4 shrink-0">
        <button data-testid={`document-preview-btn-${d.id}`} onClick={() => onPreview(d)} className="btn-link inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Anteprima</button>
        <DownloadButton kind="document" id={d.id} compact testid={`document-download-btn-${d.id}`} />
      </div>
    </div>
  );
}

function LogoGrid({ docs, onPreview }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      {docs.map((d) => (
        <div key={d.id} data-testid={`logo-item-${d.id}`} className="lux-card p-4 flex flex-col">
          <button onClick={() => onPreview(d)} aria-label="Anteprima"><FileThumb kind="document" file={d} className="h-32 w-full" /></button>
          <div className="mt-3 flex items-center gap-2">{isNew(d.created_at) && <NewBadge />}<span className="eyebrow">{fileExt(d.filename)}</span></div>
          <p className="text-sm mt-1 truncate">{d.title}</p>
          <div className="mt-3"><DownloadButton kind="document" id={d.id} compact testid={`document-download-btn-${d.id}`} /></div>
        </div>
      ))}
    </div>
  );
}

export default function Documents() {
  const { data, loading } = useFetch("/documents");
  const [preview, setPreview] = useState(null);
  const groups = useMemo(() => {
    const docs = data || [];
    return { nuovi: docs.filter((d) => isNew(d.created_at)), attestati: docs.filter((d) => d.section === "attestati"),
      contratti: docs.filter((d) => d.section === "contratti"), loghi: docs.filter((d) => d.section === "loghi"), documenti: docs.filter((d) => d.section === "documenti") };
  }, [data]);
  if (loading) return <Loader />;
  const tabs = [["nuovi", "Nuovi"], ["attestati", "Attestati"], ["contratti", "Contratti"], ["loghi", "Loghi"], ["documenti", "Documenti"]];
  return (
    <div>
      <PageHeader eyebrow="I tuoi documenti" title="Documenti" subtitle="Qui trovi gli attestati del tuo team, i contratti, i loghi ufficiali e gli altri documenti. Tutti i file sono riservati al tuo centro." />
      <Tabs defaultValue={groups.nuovi.length ? "nuovi" : "attestati"}>
        <TabsList className="bg-transparent p-0 h-auto gap-x-5 gap-y-2 sm:gap-10 border-b border-warm/40 w-full justify-start rounded-xl flex-wrap">
          {tabs.map(([k, l]) => (
            <TabsTrigger key={k} value={k} data-testid={`documents-tab-${k}`}
              className="px-0 pb-2 rounded-xl bg-transparent shadow-none data-[state=active]:shadow-none data-[state=active]:bg-transparent border-b-2 border-transparent data-[state=active]:border-gold text-[12px] uppercase tracking-[0.1em] sm:tracking-[0.16em]">
              {l} <span className={`ml-2 text-[10px] ${k === "nuovi" && groups.nuovi.length ? "text-gold" : "text-warm-text"}`}>{groups[k].length}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map(([k]) => (
          <TabsContent key={k} value={k} className="mt-8">
            {groups[k].length === 0 ? (
              <EmptyState title={k === "nuovi" ? "Nessun documento recente" : "Ancora nessun documento"} text={k === "nuovi" ? "Qui compaiono i documenti caricati negli ultimi 30 giorni." : "Appena il team Skin Lovers caricherà un file, lo troverai qui e riceverai un'email."} />
            ) : k === "loghi" ? (
              <LogoGrid docs={groups.loghi} onPreview={setPreview} />
            ) : (
              <div className={`space-y-3 ${k === "nuovi" ? "border-l-2 border-gold pl-3 sm:pl-5" : ""}`}>
                {groups[k].map((d) => <DocRow key={d.id} d={d} onPreview={setPreview} showSection={k === "nuovi"} />)}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
      <FilePreviewDialog kind="document" file={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
